import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import NotificationCenter from "@/components/NotificationCenter";
import {
  Home,
  ListTodo,
  FileText,
  CreditCard,
  FileSignature,
  Calculator,
  Building2,
  FolderOpen,
  FolderArchive,
  Wallet,
  Users,
  Building,
  UsersRound,
  Search,
  LogOut,
  User,
  ChevronDown,
  Lock,
  Crown,
  Menu,
  X,
  Landmark,
  Bell,
  Sparkles,
  Link2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { API_URL } from "@/utils/api";

// Design tokens - Lime/Neon Green theme matching landing page
const LIME = "#BFFF00";
const LIME_BG = "#F7FFE5";
const LIME_BG_LIGHT = "#EDFFC2";
const DARK = "#121212";

// Plan feature access mapping
const PLAN_ACCESS = {
  free: {
    home: true,
    tasks: true,
    invoicing: true,  // Basic invoice generator
    documents: true,
    company_profile: true,
    bill_pay: false,
    contracts: false,
    tax_hub: false,
    scorp: false,
    bank_connections: false,
    payment_methods: false,
    recipients: false,
    team: false
  },
  growth: {
    home: true,
    tasks: true,
    invoicing: true,
    documents: true,
    company_profile: true,
    bill_pay: true,
    contracts: true,
    tax_hub: true,
    scorp: false,
    bank_connections: true,
    payment_methods: true,
    recipients: true,
    team: true
  },
  scale: {
    home: true,
    tasks: true,
    invoicing: true,
    documents: true,
    company_profile: true,
    bill_pay: true,
    contracts: true,
    tax_hub: true,
    scorp: true,
    bank_connections: true,
    payment_methods: true,
    recipients: true,
    team: true
  },
  enterprise: {
    home: true,
    tasks: true,
    invoicing: true,
    documents: true,
    company_profile: true,
    bill_pay: true,
    contracts: true,
    tax_hub: true,
    scorp: true,
    bank_connections: true,
    payment_methods: true,
    recipients: true,
    team: true
  }
};

// Plan display info
const PLAN_INFO = {
  free: { name: "Free / Starter", features: ["Up to $25K annual transactions", "Basic bookkeeping", "Invoice generator", "Expense tracking"] },
  growth: { name: "Growth", features: ["Up to $100K annual transactions", "Full bookkeeping + reconciliation", "KPI dashboards", "Dedicated bookkeeper"] },
  scale: { name: "Scale", features: ["Unlimited transactions", "Advanced reporting", "Tax strategy planning", "Priority support"] },
  enterprise: { name: "Enterprise", features: ["Dedicated team of bookkeepers", "Custom integrations", "White-glove onboarding"] }
};

// Navigation items with feature keys for access control
const NAV_ITEMS = [
  { section: "Company", items: [
    { name: "Home", icon: Home, path: "/dashboard", featureKey: "home" },
    { name: "Tasks", icon: ListTodo, path: "/dashboard/tasks", featureKey: "tasks" },
    { name: "Invoicing", icon: FileText, path: "/dashboard/invoicing", featureKey: "invoicing" },
    { name: "Payment Links", icon: Link2, path: "/dashboard/payment-links", featureKey: "invoicing" },
    { name: "My Payouts", icon: Wallet, path: "/dashboard/my-payouts", featureKey: "invoicing" },
    { name: "Contracts", icon: FileSignature, path: "/dashboard/contracts", featureKey: "contracts" },
    { name: "Tax Hub", icon: Calculator, path: "/dashboard/tax-hub", featureKey: "tax_hub" },
    { name: "Documents", icon: FolderOpen, path: "/dashboard/documents", featureKey: "documents" },
    { name: "S-Corp Election", icon: Building2, path: "/dashboard/scorp", featureKey: "scorp", badge: "Beta" },
    { name: "Bill Pay", icon: CreditCard, path: "/dashboard/bill-pay", featureKey: "bill_pay", badge: "Coming Soon", disabled: true },
  ]},
  { section: "Settings", items: [
    { name: "Bank Connections", icon: Landmark, path: "/dashboard/settings/banks", featureKey: "bank_connections" },
    { name: "Payment Methods", icon: Wallet, path: "/dashboard/settings/payments", featureKey: "payment_methods" },
    { name: "Recipients", icon: Users, path: "/dashboard/settings/recipients", featureKey: "recipients" },
    { name: "Company Profile", icon: Building, path: "/dashboard/settings/company", featureKey: "company_profile" },
    { name: "DocuHub", icon: FolderArchive, path: "/dashboard/settings/docuhub", featureKey: "company_profile" },
    { name: "Team", icon: UsersRound, path: "/dashboard/settings/team", featureKey: "team" },
    { name: "Stripe Integration", icon: CreditCard, path: "/dashboard/settings/stripe", featureKey: "home", powerOnly: true },
    { name: "Plans & Billing", icon: Sparkles, path: "/dashboard/plans", featureKey: "home" },
  ]}
];

export default function DashboardLayout({ children, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [requiredPlan, setRequiredPlan] = useState("");

  // Get user's current plan and access rights
  const userPlan = user?.plan || "free";
  const planAccess = PLAN_ACCESS[userPlan] || PLAN_ACCESS.free;
  const isPaidUser = userPlan && userPlan !== "free";
  
  // Check if user has access to a feature
  const hasAccess = (featureKey) => {
    return planAccess[featureKey] === true;
  };
  
  // Get the minimum required plan for a feature
  const getRequiredPlan = (featureKey) => {
    for (const plan of ["free", "growth", "scale", "enterprise"]) {
      if (PLAN_ACCESS[plan][featureKey]) {
        return plan;
      }
    }
    return "growth";
  };

  const handleNavClick = (item, e) => {
    if (!hasAccess(item.featureKey)) {
      e.preventDefault();
      setBlockedFeature(item.name);
      setRequiredPlan(getRequiredPlan(item.featureKey));
      setShowUpgradeModal(true);
    } else {
      setSidebarOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: "POST", credentials: "include" });
      localStorage.removeItem("auth_token");
      navigate("/");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/dashboard?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      {/* Top Banner for Free Users */}
      {!isPaidUser && (
        <div className="bg-[#121212] text-white py-2 px-3 md:px-4 flex items-center justify-between gap-2">
          <p className="text-xs md:text-sm flex-1 text-white/80 line-clamp-1">
            Ready to simplify your bookkeeping?
          </p>
          <Link to="/pricing">
            <Button size="sm" className="bg-[#BFFF00] text-[#121212] hover:bg-[#a8e600] font-semibold rounded-full px-3 py-1 text-xs md:text-sm whitespace-nowrap">
              See Plans
            </Button>
          </Link>
        </div>
      )}

      <div className="flex">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-3 left-3 z-50 p-2.5 bg-[#121212] rounded-xl text-white shadow-lg"
          style={{ top: isPaidUser ? '12px' : '56px' }}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-30"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Lime Green Sidebar */}
        <aside 
          className={`fixed left-0 w-64 h-screen z-40 transform transition-transform duration-300 ease-in-out flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} 
          style={{ 
            top: isPaidUser ? 0 : '44px', 
            height: isPaidUser ? '100vh' : 'calc(100vh - 44px)',
            background: 'linear-gradient(180deg, #F7FFE5 0%, #EDFFC2 100%)'
          }}
        >
          {/* Grid Pattern Overlay */}
          <div 
            className="absolute inset-0 opacity-30 pointer-events-none" 
            style={{ 
              backgroundImage: 'linear-gradient(#BFFF00 1px, transparent 1px), linear-gradient(90deg, #BFFF00 1px, transparent 1px)', 
              backgroundSize: '40px 40px' 
            }} 
          />
          
          {/* Logo */}
          <div className="relative p-6 border-b border-[#BFFF00]/30">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#BFFF00] rounded-xl flex items-center justify-center shadow-lg shadow-[#BFFF00]/30">
                <span className="text-[#121212] font-extrabold text-lg font-['Manrope']">F</span>
              </div>
              <span className="text-[#121212] font-extrabold text-xl tracking-tight font-['Manrope']">FinAI</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="relative flex-1 overflow-y-auto py-6 px-3">
            {NAV_ITEMS.map((section, idx) => (
              <div key={idx} className="mb-6">
                <p className="px-3 text-xs font-semibold text-[#121212]/50 uppercase tracking-widest mb-3">
                  {section.section}
                </p>
                <div className="space-y-1">
                  {section.items.filter(item => !item.powerOnly || user?.plan === "power").map((item) => {
                    const isActive = location.pathname === item.path || 
                      (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
                    const isLocked = !hasAccess(item.featureKey);
                    const isDisabled = item.disabled;
                    
                    return (
                      <button
                        key={item.path}
                        onClick={(e) => {
                          if (isDisabled) {
                            e.preventDefault();
                            // Do nothing for disabled items
                            return;
                          }
                          if (isLocked) {
                            e.preventDefault();
                            setBlockedFeature(item.name);
                            setRequiredPlan(getRequiredPlan(item.featureKey));
                            setShowUpgradeModal(true);
                          } else {
                            navigate(item.path);
                            setSidebarOpen(false);
                          }
                        }}
                        disabled={isDisabled}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                          isDisabled
                            ? 'text-[#121212]/40 cursor-not-allowed'
                            : isActive 
                              ? 'bg-[#121212] text-white shadow-lg' 
                              : 'text-[#121212]/70 hover:text-[#121212] hover:bg-[#BFFF00]/40'
                        }`}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <item.icon className={`w-5 h-5 ${isActive ? 'text-[#BFFF00]' : ''} ${isDisabled ? 'opacity-40' : ''}`} strokeWidth={1.5} />
                        <span className="flex-1 text-left">{item.name}</span>
                        {item.badge && (
                          <Badge className={`text-[10px] px-1.5 py-0 ${
                            item.badge === "Coming Soon" 
                              ? 'bg-gray-200 text-gray-500'
                              : isActive ? 'bg-[#BFFF00] text-[#121212]' : 'bg-[#121212]/10 text-[#121212]'
                          }`}>
                            {item.badge}
                          </Badge>
                        )}
                        {isLocked && !isDisabled && (
                          <Lock className={`w-3.5 h-3.5 ${isActive ? 'text-white/60' : 'text-[#121212]/40'}`} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User Profile with Logout */}
          <div className="relative p-4 border-t border-[#BFFF00]/30">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-[#121212] rounded-xl flex items-center justify-center text-[#BFFF00] font-bold text-sm shadow-md">
                {user?.name?.[0] || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#121212] truncate">{user?.name || "User"}</p>
                <p className="text-xs text-[#121212]/50 truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl"
              data-testid="user-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 min-h-screen w-full overflow-x-hidden">
          {/* Top Header */}
          <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-[#BFFF00]/20">
            <div className="flex items-center justify-between px-3 md:px-4 lg:px-8 h-14 md:h-16">
              {/* Search - hide on very small screens */}
              <form onSubmit={handleSearch} className="hidden sm:flex flex-1 max-w-md ml-12 lg:ml-0">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#121212]/40" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-9 md:h-10 bg-[#F7FFE5] border-[#BFFF00]/30 rounded-xl focus:ring-2 focus:ring-[#BFFF00]/50 focus:border-[#BFFF00] text-sm"
                    data-testid="search-input"
                  />
                </div>
              </form>

              {/* Mobile spacer for menu button */}
              <div className="w-10 sm:hidden" />

              {/* Right Actions */}
              <div className="flex items-center gap-2 md:gap-3">
                <NotificationCenter />
                
                {isPaidUser && (
                  <Badge className="hidden md:flex bg-[#BFFF00]/20 text-[#121212] border border-[#BFFF00]/50 rounded-full px-2 md:px-3 py-1 font-semibold text-xs">
                    <Crown className="w-3 h-3 mr-1 text-[#121212]" />
                    <span className="capitalize">{user?.plan}</span>
                  </Badge>
                )}

                <div className="w-8 h-8 md:w-9 md:h-9 bg-[#121212] rounded-xl flex items-center justify-center text-[#BFFF00] font-bold text-xs md:text-sm shadow-md">
                  {user?.name?.[0] || "U"}
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <div className="p-3 md:p-4 lg:p-8 max-w-full overflow-x-hidden">
            {children}
          </div>
        </main>
      </div>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md rounded-3xl border-2 border-[#BFFF00]/30">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-['Manrope']">Upgrade to Access {blockedFeature}</DialogTitle>
            <DialogDescription className="text-[#121212]/60">
              This feature requires the <span className="font-semibold text-[#121212] capitalize">{requiredPlan || "Growth"}</span> plan or higher.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Current Plan */}
            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-xl">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-300 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Current Plan</p>
                  <p className="font-semibold capitalize">{PLAN_INFO[userPlan]?.name || "Free / Starter"}</p>
                </div>
              </div>
            </div>
            
            {/* Required Plan Features */}
            {requiredPlan && PLAN_INFO[requiredPlan] && (
              <div className="p-4 bg-[#F7FFE5] rounded-2xl border-2 border-dashed border-[#BFFF00]/50">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-5 h-5 text-[#121212]" />
                  <p className="font-semibold text-[#121212] font-['Manrope'] capitalize">{PLAN_INFO[requiredPlan].name} Plan</p>
                </div>
                <ul className="space-y-2">
                  {PLAN_INFO[requiredPlan].features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[#121212]/70">
                      <div className="w-1.5 h-1.5 bg-[#BFFF00] rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowUpgradeModal(false)} className="flex-1 rounded-full border-2 border-[#121212]/20 hover:border-[#BFFF00] hover:bg-[#F7FFE5]">
                Maybe Later
              </Button>
              <Link to="/dashboard/plans" className="flex-1">
                <Button className="w-full bg-[#121212] hover:bg-[#2a2a2a] rounded-full font-semibold" onClick={() => setShowUpgradeModal(false)}>
                  View Plans
                </Button>
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
